// src/theme/getDesignTokens.ts
import { PaletteMode } from '@mui/material';

// Brand colors
const brandColors = {
  navy: '#030766',
  lime: '#e6f226',
  limeHover: '#d4e022',
  limeMuted: 'rgba(230, 242, 38, 0.15)',
  navyMuted: 'rgba(3, 7, 102, 0.08)',
};

// Shadcn-inspired palette (zinc gray scale + neutral brand). Adjust to your brand if needed.
const shadcn = {
  light: {
    primary: '#111827', // near zinc-900 for strong accents
    secondary: '#6B7280', // zinc-500
    bgDefault: '#FFFFFF', // pure white - Attio style
    bgPaper: '#FFFFFF',
    textPrimary: '#0B0F19', // neutral-950
    textSecondary: '#6B7280',
    border: '#E5E7EB', // gray-200
    success: brandColors.lime, // lime for success states
    successContrastText: brandColors.navy
  },
  dark: {
    primary: '#E5E7EB', // gray-200 for contrast on dark
    secondary: '#9CA3AF', // gray-400
    bgDefault: '#0a0a0a', // pure dark - Attio style
    bgPaper: '#0a0a0a',
    textPrimary: '#F9FAFB', // gray-50
    textSecondary: '#D1D5DB', // gray-300
    border: '#1F2937', // gray-800
    success: brandColors.lime, // lime for success states
    successContrastText: brandColors.navy
  }
};

export const getDesignTokens = (mode: PaletteMode) => {
  const palette = mode === 'light' ? shadcn.light : shadcn.dark;
  return {
    palette: {
      mode,
      primary: { main: palette.primary },
      secondary: { main: palette.secondary },
      success: { 
        main: palette.success, 
        contrastText: palette.successContrastText,
        light: brandColors.limeMuted,
        dark: brandColors.limeHover
      },
      divider: palette.border,
      // Custom key retained
      menuBackground: {
        main: mode === 'light' ? '#F3F4F6' : '#0F172A'
      },
      // Brand colors accessible in theme
      brand: {
        navy: brandColors.navy,
        lime: brandColors.lime,
        limeHover: brandColors.limeHover,
        limeMuted: brandColors.limeMuted,
        navyMuted: brandColors.navyMuted
      },
      background: {
        default: palette.bgDefault,
        paper: palette.bgPaper
      },
      text: {
        primary: palette.textPrimary,
        secondary: palette.textSecondary
      }
    },
    typography: {
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      h1: {
        fontWeight: 700,
        letterSpacing: '-0.025em'
      },
      h2: {
        fontWeight: 700,
        letterSpacing: '-0.025em'
      },
      h3: {
        fontWeight: 600,
        letterSpacing: '-0.02em'
      },
      h4: {
        fontWeight: 600,
        letterSpacing: '-0.02em'
      },
      h5: {
        fontWeight: 600,
        letterSpacing: '-0.01em'
      },
      h6: {
        fontWeight: 600,
        letterSpacing: '-0.01em'
      },
      body1: {
        fontWeight: 400,
        letterSpacing: '-0.01em'
      },
      body2: {
        fontWeight: 400,
        letterSpacing: '-0.005em'
      },
      button: {
        fontWeight: 500,
        letterSpacing: '-0.005em'
      },
      caption: {
        fontWeight: 400,
        letterSpacing: '0em'
      },
      overline: {
        fontWeight: 500,
        letterSpacing: '0.02em',
        textTransform: 'uppercase' as const
      }
    },
    shape: {
      borderRadius: 10
    },
    components: {
      MuiModal: {
        defaultProps: {
          keepMounted: false,
          disableEnforceFocus: true,
          disableScrollLock: true
        }
      },
      MuiDrawer: {
        defaultProps: {
          keepMounted: false,
          disableEnforceFocus: true,
          disableScrollLock: true
        }
      },
      MuiPopover: {
        defaultProps: {
          disableScrollLock: true
        }
      },
      MuiMenu: {
        defaultProps: {
          disableScrollLock: true
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 8,
            // Required by MUI v7 types (CSSObjectWithVariants)
            variants: []
          },
          contained: {
            boxShadow: 'none',
            variants: []
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            variants: []
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${palette.border}`,
            variants: []
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 40,
            variants: []
          },
          indicator: {
            display: 'none',
            variants: []
          },
          scrollButtons: {
            color: palette.textSecondary,
            '&.Mui-disabled': {
              opacity: 0.3
            },
            variants: []
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 40,
            borderRadius: 8,
            margin: 2,
            paddingInline: 12,
            '&.Mui-selected': {
              backgroundColor: mode === 'light' ? '#F3F4F6' : '#0B1220',
              color: palette.textPrimary
            },
            variants: []
          }
        }
      },
      MuiDialog: {
        defaultProps: {
          disableEnforceFocus: true,
          disableScrollLock: true
        },
        styleOverrides: {
          paper: {
            borderRadius: 12,
            border: `1px solid ${palette.border}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            variants: []
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            variants: []
          }
        }
      }
    }
  };
};

// Add TypeScript declaration for custom palette keys
declare module '@mui/material/styles' {
  interface Palette {
    menuBackground: {
      main: string;
    };
    brand: {
      navy: string;
      lime: string;
      limeHover: string;
      limeMuted: string;
      navyMuted: string;
    };
  }
  interface PaletteOptions {
    menuBackground?: {
      main: string;
    };
    brand?: {
      navy: string;
      lime: string;
      limeHover: string;
      limeMuted: string;
      navyMuted: string;
    };
  }
}
